import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  SmsSchedulerState, 
  SchedulerConfig, 
  SchedulerConfigCreate,
  SchedulerConfigUpdate,
  SchedulerLog,
  SchedulerLogFilters,
  SchedulerStatus
} from '@/types/smsScheduler';

const initialState: SmsSchedulerState = {
  // Configurations
  configurations: [],
  configurationsLoading: false,
  configurationsError: null,
  
  // Current config being edited
  currentConfig: null,
  configLoading: false,
  configError: null,
  
  // Logs
  logs: [],
  logsLoading: false,
  logsError: null,
  logsFilters: {
    limit: 100
  },
  
  // Status
  status: null,
  statusLoading: false,
  statusError: null,
  
  // UI State
  isCreateDialogOpen: false,
  isEditDialogOpen: false,
  isDeleteDialogOpen: false,
  isLogsDialogOpen: false,
  
  // Selected items
  selectedConfigId: null,
  configToDelete: null
};

const smsSchedulerSlice = createSlice({
  name: 'smsScheduler',
  initialState,
  reducers: {
    // Configuration Actions
    fetchSchedulerConfigsRequested: (state) => {
      state.configurationsLoading = true;
      state.configurationsError = null;
    },
    fetchSchedulerConfigsSucceeded: (state, action: PayloadAction<SchedulerConfig[]>) => {
      state.configurationsLoading = false;
      state.configurations = action.payload;
    },
    fetchSchedulerConfigsFailed: (state, action: PayloadAction<string>) => {
      state.configurationsLoading = false;
      state.configurationsError = action.payload;
    },
    
    // Create Configuration Actions
    createSchedulerConfigRequested: (state, action: PayloadAction<SchedulerConfigCreate>) => {
      state.configLoading = true;
      state.configError = null;
    },
    createSchedulerConfigSucceeded: (state, action: PayloadAction<SchedulerConfig>) => {
      state.configLoading = false;
      state.configurations.push(action.payload);
      state.isCreateDialogOpen = false;
    },
    createSchedulerConfigFailed: (state, action: PayloadAction<string>) => {
      state.configLoading = false;
      state.configError = action.payload;
    },
    
    // Update Configuration Actions
    updateSchedulerConfigRequested: (state, action: PayloadAction<{id: number, config: SchedulerConfigUpdate}>) => {
      state.configLoading = true;
      state.configError = null;
    },
    updateSchedulerConfigSucceeded: (state, action: PayloadAction<SchedulerConfig>) => {
      state.configLoading = false;
      const index = state.configurations.findIndex(config => config.id === action.payload.id);
      if (index !== -1) {
        state.configurations[index] = action.payload;
      }
      state.isEditDialogOpen = false;
      state.currentConfig = null;
    },
    updateSchedulerConfigFailed: (state, action: PayloadAction<string>) => {
      state.configLoading = false;
      state.configError = action.payload;
    },
    
    // Delete Configuration Actions
    deleteSchedulerConfigRequested: (state, action: PayloadAction<number>) => {
      state.configLoading = true;
      state.configError = null;
    },
    deleteSchedulerConfigSucceeded: (state, action: PayloadAction<number>) => {
      state.configLoading = false;
      state.configurations = state.configurations.filter(config => config.id !== action.payload);
      state.isDeleteDialogOpen = false;
      state.configToDelete = null;
    },
    deleteSchedulerConfigFailed: (state, action: PayloadAction<string>) => {
      state.configLoading = false;
      state.configError = action.payload;
    },
    
    // Logs Actions
    fetchSchedulerLogsRequested: (state, action: PayloadAction<SchedulerLogFilters>) => {
      state.logsLoading = true;
      state.logsError = null;
      state.logsFilters = action.payload;
    },
    fetchSchedulerLogsSucceeded: (state, action: PayloadAction<SchedulerLog[]>) => {
      state.logsLoading = false;
      state.logs = action.payload;
    },
    fetchSchedulerLogsFailed: (state, action: PayloadAction<string>) => {
      state.logsLoading = false;
      state.logsError = action.payload;
    },
    
    // Status Actions
    fetchSchedulerStatusRequested: (state) => {
      state.statusLoading = true;
      state.statusError = null;
    },
    fetchSchedulerStatusSucceeded: (state, action: PayloadAction<SchedulerStatus>) => {
      state.statusLoading = false;
      state.status = action.payload;
    },
    fetchSchedulerStatusFailed: (state, action: PayloadAction<string>) => {
      state.statusLoading = false;
      state.statusError = action.payload;
    },
    
    // Manual Trigger Actions
    manuallyTriggerSchedulerRequested: (state, action: PayloadAction<number>) => {
      state.configLoading = true;
      state.configError = null;
    },
    manuallyTriggerSchedulerSucceeded: (state) => {
      state.configLoading = false;
    },
    manuallyTriggerSchedulerFailed: (state, action: PayloadAction<string>) => {
      state.configLoading = false;
      state.configError = action.payload;
    },
    
    // UI State Actions
    openCreateDialog: (state) => {
      state.isCreateDialogOpen = true;
      state.configError = null;
    },
    closeCreateDialog: (state) => {
      state.isCreateDialogOpen = false;
      state.configError = null;
    },
    
    openEditDialog: (state, action: PayloadAction<SchedulerConfig>) => {
      state.isEditDialogOpen = true;
      state.currentConfig = action.payload;
      state.configError = null;
    },
    closeEditDialog: (state) => {
      state.isEditDialogOpen = false;
      state.currentConfig = null;
      state.configError = null;
    },
    
    openDeleteDialog: (state, action: PayloadAction<SchedulerConfig>) => {
      state.isDeleteDialogOpen = true;
      state.configToDelete = action.payload;
      state.configError = null;
    },
    closeDeleteDialog: (state) => {
      state.isDeleteDialogOpen = false;
      state.configToDelete = null;
      state.configError = null;
    },
    
    openLogsDialog: (state) => {
      state.isLogsDialogOpen = true;
    },
    closeLogsDialog: (state) => {
      state.isLogsDialogOpen = false;
    },
    
    setSelectedConfigId: (state, action: PayloadAction<number | null>) => {
      state.selectedConfigId = action.payload;
    },
    
    clearSchedulerError: (state) => {
      state.configError = null;
      state.configurationsError = null;
      state.logsError = null;
      state.statusError = null;
    },
    
    resetSmsScheduler: (state) => {
      Object.assign(state, initialState);
    }
  }
});

export const {
  // Configuration
  fetchSchedulerConfigsRequested,
  fetchSchedulerConfigsSucceeded,
  fetchSchedulerConfigsFailed,
  
  // Create
  createSchedulerConfigRequested,
  createSchedulerConfigSucceeded,
  createSchedulerConfigFailed,
  
  // Update
  updateSchedulerConfigRequested,
  updateSchedulerConfigSucceeded,
  updateSchedulerConfigFailed,
  
  // Delete
  deleteSchedulerConfigRequested,
  deleteSchedulerConfigSucceeded,
  deleteSchedulerConfigFailed,
  
  // Logs
  fetchSchedulerLogsRequested,
  fetchSchedulerLogsSucceeded,
  fetchSchedulerLogsFailed,
  
  // Status
  fetchSchedulerStatusRequested,
  fetchSchedulerStatusSucceeded,
  fetchSchedulerStatusFailed,
  
  // Manual Trigger
  manuallyTriggerSchedulerRequested,
  manuallyTriggerSchedulerSucceeded,
  manuallyTriggerSchedulerFailed,
  
  // UI State
  openCreateDialog,
  closeCreateDialog,
  openEditDialog,
  closeEditDialog,
  openDeleteDialog,
  closeDeleteDialog,
  openLogsDialog,
  closeLogsDialog,
  setSelectedConfigId,
  clearSchedulerError,
  resetSmsScheduler
} = smsSchedulerSlice.actions;

export default smsSchedulerSlice.reducer;

